import { Button, Card } from "antd"
import './style.css';
import { TOnBoardSymptomsProps } from "@stores/interfaces";
import { useTranslation } from "react-i18next";
import { useTypedDispatch, useTypedSelector } from "@stores/index";
import FeatureCard from "./FeatureCard";
import { setPainStatusButton, wellCheckNess } from "@stores/onBoard/onBoard";
import { useEffect, useMemo, useState } from "react";
import { CONTENT_MANAGER_MOCK_DATA, PLAN_TYPES } from "@pages/Settings/contentManagerMockData";
import { getPlanByPlanType, getPlansByUserId, updatePlanByUserId } from "@stores/settings/setings";
import { PLANS } from "@stores/constants";

export interface FeatureProps {
  title: string;
  planType?: string;
  backgroundStyle: string;
  hoverBackgroundStyle: string;
  option: string;
  imageSrc: string;
}

export default function PatientPainStatus(props: TOnBoardSymptomsProps) {
  const { setActiveStep, setProgressPercent , setNavigatorDirection} = props
  const { t } = useTranslation()
  const dispatch = useTypedDispatch();
  const user = useTypedSelector((state) => state.user)
  const savedUserPlans = useTypedSelector((state) => state.settings.settings.savedUserPlans)
  const planType = savedUserPlans?.planType; 
  const [painStatus, setPainStatus] = useState<FeatureProps[]>([]);
  const { plans } = useTypedSelector(
    state => state.settings.settings,
  );
  const { selectedUser } = useTypedSelector(state => state.contacts.main);

  useEffect(() => {
    initializePlans();
  }, []);

  const initializePlans = async () => {
    const id = user.isPhysioterapist ? selectedUser?.id : user?.id;
    await dispatch(getPlansByUserId(id));
  };

  useEffect(() => {
    const hasValidPlans = plans.every(plan => plan?.id);
    const sourcePlans = hasValidPlans ? plans : [];
    const mockPlans = CONTENT_MANAGER_MOCK_DATA.plans || [];

    const finalPlans = [];

    for (let i = 0; i < 3; i++) {
      const plan = sourcePlans[i] || {};
      const mock = mockPlans[i] || {};

      finalPlans.push({
        ...mock,
        ...plan,
        title: plan.title || mock.title,
        option: plan.description || mock.description,
        imageSrc: plan.thumbnail || mock.thumbnail || '',
        backgroundStyle: features[i]?.backgroundStyle,
        hoverBackgroundStyle: features[i]?.hoverBackgroundStyle,
        planType: plan.planType || mock.planType || '',
      });
    }

    setPainStatus(finalPlans);
  }, [plans]);

  useEffect(() => {
		fetchPlans();
	}, []);

	const fetchPlans = async () => {
		for (const planType of PLAN_TYPES) {
			await dispatch(getPlanByPlanType(planType));
		}
	};

 const handleOptions = (str: string) => {
  const formattedStr = str
    .replace(/<p>/g, '<li style="margin-bottom: 10px;">')
    .replace(/<\/p>/g, '</li>');
  
  return <ul style={{listStyle:'disc'}} dangerouslySetInnerHTML={{ __html: formattedStr }} />;
};


  const nextButtonStyle = {
		height: '43px',
		background: 'var(--onboard-button-color)',
		color: '#fff',
		width: '230px',
    border: 'none',
	};

  const features = [
    {
      backgroundStyle: 'linear-gradient(135deg, rgba(105, 65, 198, 0.9), rgba(84, 205, 242, 0.9))',
      hoverBackgroundStyle: 'linear-gradient(45deg, rgba(84, 205, 242, 0.9), rgba(105, 65, 198, 0.9))',
    },
    {
      backgroundStyle: 'linear-gradient(135deg, rgba(243, 33, 215, 0.9), rgba(255, 236, 97, 0.9))',
      hoverBackgroundStyle: 'linear-gradient(45deg, rgba(255, 236, 97, 0.9), rgba(243, 33, 215, 0.9))',
    },
    {
      backgroundStyle: 'linear-gradient(135deg, rgba(154, 78, 255, 0.9), rgba(36, 255, 114, 0.9))',
      hoverBackgroundStyle: 'linear-gradient(45deg, rgba(36, 255, 114, 0.9), rgba(154, 78, 255, 0.9))',
    }
  ];

 const filteredFeatures = useMemo(() => {
    switch (planType) {
      case PLANS.SCREENING:
        return [painStatus[0]];
      case PLANS.EARLYINTERVENTION:
        return painStatus.slice(0, 2);
      case PLANS.VIRTUALPT:
      default:
        return painStatus;
    }
  }, [painStatus, planType]);

  return (
    <>
      <div className="container mx-auto mt-4">
        <div className="whole-day-header-container mb-10">
          <h1 className="whole-day-header-title">{t('Patient.data.onboard.choosePath')}</h1>
          <h3 className="whole-day-header-subtitle">
            {filteredFeatures?.length > 1 ? t('Patient.data.onboard.selectCategory') : t('Patient.data.onboard.chooseCategory')}
          </h3>
        </div>
        {filteredFeatures?.length > 1 ? (
          <>
            <div className="flex justify-center space-x-5">
              {filteredFeatures?.map((feature, index) => (
                <FeatureCard
                  key={index}
                  title={feature.title}
                  option={feature.option}
                  planType={feature.planType || ''}
                  hoverBackgroundStyle={feature.hoverBackgroundStyle}
                  backgroundStyle={feature.backgroundStyle}
                  imageSrc={feature.imageSrc}
                  setProgressPercent={setProgressPercent}
                  setActiveStep={setActiveStep}
                />
              ))}
            </div>
            <div className="pain-subcontainer">
              <Button className="mt-4 mb-4 bg-gray-200 text-primary-800 rounded-full font-inter text-base font-bold leading-6" onClick={() => { setActiveStep(1) }} size="large" block style={nextButtonStyle}>
                {t('Patient.data.onboard.back')}
              </Button>
            </div>
          </>
        ) : filteredFeatures?.length === 1 && filteredFeatures[0] &&
        <div className="flex flex-col items-center justify-center">
          <div
            className="flex justify-center space-x-5 mb-10"
            style={{ width: "80%" }}
          >
            <Card className="feature-card" style={{ width: "80%" }}>
              <div className="select-none">
                <div className="h-auto" style={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                  <div className="flex h-auto">
                    <img src={painStatus[0]?.imageSrc || ''} alt="" className="object-cover w-[50%] h-[600px]" />
                    <div className="w-[50%] flex items-center justify-center flex-col p-4" style={{
                      backgroundImage: "url('/images/dashboard/card-bg.png')",
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}>
                      <div className="flex justify-center items-center text-white font-bold p-1 gap-15">
                        <h3 className="font-bold text-white mt-2 text-[22px] text-center">{painStatus[0]?.title?.toUpperCase()}</h3>
                      </div>
                      <div className="overflow-hidden sm:min-h-[150px] md:min-h-[180px] lg:min-h-[118px]">
                        <div className="text-white font-inter text-[15px] font-light leading-[18.15px] mb-1 mr-1 ml-1 p-1 text-base">
                          <p
                            className="onboard-ul bg-[#6941C666] text-white px-8 py-2 rounded-lg"
                            style={{ opacity: 0.9 }}
                          >
                            {handleOptions(painStatus[0]?.option)}
                          </p>
                         </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          <div className="whole-day-btn-container mt-0">
            <Button
              className="text-primary-800 rounded-full font-inter text-base font-bold leading-6" onClick={() => { setNavigatorDirection('backward') ; setActiveStep(1) }} 
              size="large" block style={nextButtonStyle}>
              {t("Patient.data.onboard.back")}
            </Button>

            <Button size="large" className="rounded-full font-inter text-base font-bold leading-6" block style={nextButtonStyle}
              onClick={async () => {
                if (painStatus.length <= 3) {
                  setNavigatorDirection('forward')
                  setProgressPercent(50);
                  setActiveStep(5);
                  dispatch(setPainStatusButton('noPain'));
                  const params = { userId: user?.id, isWellCheckNess: true }
                  await dispatch(wellCheckNess(params))
                  dispatch(updatePlanByUserId({ userId: user?.id, planType: painStatus[0]?.planType! }))
                } else {
                  setNavigatorDirection('forward')
                  setProgressPercent(30);
                  setActiveStep(5);
                  dispatch(setPainStatusButton(''));
                  const params = { userId: user?.id, isWellCheckNess: false }
                  await dispatch(wellCheckNess(params))
                  dispatch(updatePlanByUserId({ userId: user?.id, planType: PLANS.VIRTUALPT }))
                }
              }}
            >
              {t("Patient.data.onboard.next")}
            </Button>
          </div>
        </div>
        }
      </div>
    </>
  );
}